import React, { useState, useEffect, useContext, useCallback } from "react";
import { register, getUsers, delUser } from "../api";
import { AuthContext } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash2 } from "lucide-react";

export default function Users() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);

  // Formulário
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [confirmaSenha, setConfirmaSenha] = useState("");

  //const [editingId, setEditingId] = useState(null); // Futuro: habilitar edição inline

  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [msg, setMsg] = useState(null);

  // 🔹 Buscar usuários
  const fetchUsers = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await getUsers(user.token);
      setUsers(data);
    } catch (error) {
      setMsg({ type: "error", text: error.message });
    } finally {
      setLoadingList(false);
    }
  }, [user.token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // 🔹 Cadastrar usuário
  async function handleSubmit(e) {
    e.preventDefault();
    setMsg(null);

    if (!nome.trim() || !email.trim() || !senha || !confirmaSenha) {
      setMsg({ type: "error", text: "Preencha todos os campos." });
      return;
    }

    if (senha !== confirmaSenha) {
      setMsg({ type: "error", text: "A confirmação da senha não confere." });
      return;
    }

    setLoading(true);

    try {
      await register(nome, email, senha);

      setMsg({ type: "success", text: "Usuário cadastrado com sucesso!" });

      // Limpa formulário
      setNome("");
      setEmail("");
      setSenha("");
      setConfirmaSenha("");

      fetchUsers(); // 🔥 atualiza lista
    } catch (error) {
      setMsg({ type: "error", text: error.message });
    } finally {
      setLoading(false);
    }
  }

  // 🔹 Deletar usuário
  async function handleDelete(id) {
    if (!window.confirm("Deseja excluir este usuário?")) return;

    try {
      await delUser(user.token, id);
      setMsg({ type: "success", text: "Usuário excluído com sucesso!" });
      setUsers(prev => prev.filter(u => u.id !== id));
    } catch (error) {
      setMsg({ type: "error", text: error.message });
    }
  }

  return (
    <div className="page-container">
      <div className="dashboard-container">
        <div className="user-card" style={{ maxWidth: "1000px", margin: "0 auto" }}>

          {/* Cabeçalho */}
          <div style={{ 
            justifyContent: "space-between", 
            alignItems: "center", 
            marginBottom: "2rem" 
          }}>
            <h3>Novo Usuário</h3>
          </div>

          {/* ==================== FORMULÁRIO ==================== */}
          <div className="item-row add-row" style={{ marginBottom: "2.5rem", gridTemplateColumns: "1fr 2fr 1fr 1fr auto" }}>
            
            <div className="item-input">
              <label>Nome Completo</label>
              <input
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Nome do usuário"
                className="input-text"
              />
            </div>

            <div className="item-input">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@exemplo.com"
                className="input-text"
              />
            </div>

            <div className="item-input">
              <label>Senha</label>
              <input
                type="password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                placeholder="Senha"
                className="input-text"
              />
            </div>

            <div className="item-input">
              <label>Confirmar Senha</label>
              <input
                type="password"
                value={confirmaSenha}
                onChange={(e) => setConfirmaSenha(e.target.value)}
                placeholder="Confirme a senha"
                className="input-text"
              />
            </div>

            <div className="item-actions">
              <button 
                type="button"
                className="btn-small"
                onClick={handleSubmit}
                disabled={loading || !nome.trim() || !email.trim() || !senha || !confirmaSenha}
              >
                {loading ? (
                  <>
                    <span className="spinner" style={{ marginRight: "8px" }}></span>
                    Cadastrando...
                  </>
                ) : (
                  "Cadastrar Usuário"
                )}
              </button>
            </div>
          </div>

          {/* Mensagem de feedback */}
          {msg && (
            <div 
              className={msg.type === "error" ? "error-msg" : "success-msg"} 
              style={{ marginBottom: "2rem" }}
            >
              {msg.text}
            </div>
          )}

          {/* ==================== LISTA DE USUÁRIOS ==================== */}
          <div style={{ marginTop: "2rem" }}>
            <h3 style={{ marginBottom: "1.5rem" }}>Lista de Usuários</h3>

            {loadingList ? (
              <div className="center-content" style={{ padding: "3rem 0" }}>
                <div className="spinner"></div>
                <p>Carregando usuários...</p>
              </div>
            ) : (
              <div className="items-list">
                {users.length === 0 ? (
                  <p style={{ 
                    opacity: 0.7, 
                    textAlign: "center", 
                    padding: "3rem 0",
                    fontSize: "1.05rem"
                  }}>
                    Nenhum usuário cadastrado ainda.
                  </p>
                ) : (
                  users
                    .sort((a, b) => b.id - a.id)
                    .map((u) => (
                      <div key={u.id} className="item-row" style={{ gridTemplateColumns: "1fr 2fr auto" }}>

                        <div className="item-input">
                          <label>Nome</label>
                          <input value={u.nome} disabled />
                        </div>

                        <div className="item-input">
                          <label>Email</label>
                          <input value={u.email} disabled />
                        </div>

                        <div className="item-actions">
                          <button
                            className="btn-small"
                            onClick={() => navigate(`/edituser/${u.id}`)}
                            title="Editar Usuário"
                          >
                            <Pencil size={18} />
                          </button>

                          <button
                            className="btn-danger btn-small"
                            onClick={() => handleDelete(u.id)}
                            title="Excluir Usuário"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))
                )}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}